import type { ReactNode } from "react";
import { Button as AriaButton } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon } from "@core/assets";
import { cn } from "@core/utils";

interface PageHeaderProps {
  title: string;
  /** Draws the round back arrow before the title (cohort page, settings). */
  onBack?: () => void;
  /** Chips after the title: the cohort code, its status. */
  chips?: ReactNode;
  /** Actions on the right: « Inviter un membre », « Paramètres », « Enregistrer ». */
  actions?: ReactNode;
  className?: string;
}

/**
 * Title row of a staff page: optional back arrow, the page title, chips, then the actions on the
 * right. Every screen in the workflow frames starts with it. It wraps below `sm` instead of
 * squeezing: the actions drop under the title at 320px.
 */
export function PageHeader({
  title,
  onBack,
  chips,
  actions,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation("common");

  return (
    <header
      className={cn(
        "flex flex-wrap items-center justify-between gap-md",
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-sm">
        {onBack ? (
          <AriaButton
            onPress={onBack}
            aria-label={t("actions.back")}
            className={cn(
              "flex size-10 shrink-0 items-center justify-center rounded-full",
              "border border-(--border-default) bg-surface-card text-(--text-primary)",
              "outline-none transition-colors hover:bg-ink-50 pressed:bg-ink-100",
              "focus-visible:ring-2 focus-visible:ring-focus-ring"
            )}
          >
            <Icon name="arrow-left" size={20} />
          </AriaButton>
        ) : null}
        <h1 className="display-sm-semibold min-w-0 truncate text-(--text-primary)">
          {title}
        </h1>
        {chips ? (
          <div className="flex shrink-0 items-center gap-xs">{chips}</div>
        ) : null}
      </div>
      {actions ? (
        <div className="flex items-center gap-sm">{actions}</div>
      ) : null}
    </header>
  );
}

export type { PageHeaderProps };
